import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { EMPTY, Subject, switchMap, take } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import {
  TellentAccessService,
  TellentManageRedirectService,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import {
  resolveAssignmentFailureReason,
  TellentPositionsConflictsService,
  TellentPositionsDataFacade,
} from '@core/tellent-positions-logic';
import {
  AssignMembersOptions,
  ConfirmPositionChangeMember,
  TellentPosition,
  TellentPositionMember,
} from '@core/tellent-positions-types';
import { PositionDetailsMemberItemComponent } from '@core/tellent-positions-ui';
import { TellentDevFlag } from '@core/tellent-types';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  ButtonComponent,
  EmptyStateComponent,
  IconComponent,
  InputComponent,
  NotificationsController,
} from '@core/user-interface-legacy';

import { PositionsDialogsService } from '../../../services/positions-dialogs.service';

@Component({
  selector: 'tlc-position-details-employees',
  templateUrl: 'position-details-employees.component.html',
  styleUrls: ['position-details-employees.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    EmptyStateComponent,
    TranslatePipe,
    InputComponent,
    PositionDetailsMemberItemComponent,
  ],
  providers: [TellentManageRedirectService],
})
export class PositionDetailsEmployeesComponent {
  private readonly positionsDialogService = inject(PositionsDialogsService);
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);
  private readonly positionsConflictsService = inject(TellentPositionsConflictsService);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly notificationsController = inject(NotificationsController);
  private readonly t = injectTranslate();
  private readonly redirectService = inject(TellentManageRedirectService);

  private readonly hasPositionsHistoryFlag = injectDevFlagSignal(
    TellentDevFlag.OnePositionsHistory,
  );

  private readonly searchQuery$ = new Subject<string>();

  public readonly canManageUsers = inject(TellentAccessService).canManageTellentUsers;

  public readonly positionData = input.required<TellentPosition | null>();

  public assignedMembers = computed<TellentPositionMember[]>(
    () => this.positionData()?.members || [],
  );
  public hasMembersAssigned = computed(() => this.assignedMembers().length > 0);

  public filteredMembers = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return this.assignedMembers();
    return this.assignedMembers().filter(m =>
      resolveMemberFullName(m).toLowerCase().includes(query),
    );
  });

  public searchQuery = toSignal(this.searchQuery$.pipe(debounceTime(300)), { initialValue: '' });

  public onSearchChange(value: string): void {
    this.searchQuery$.next(value);
  }

  public assignMembers(): void {
    this.positionsDialogService
      .openAssignMembersDialog(this.positionData()!)
      .pipe(
        take(1),
        switchMap(result => {
          if (!result?.membersGuids?.length) return EMPTY;
          return this.confirmAssignment(result.membersGuids);
        }),
      )
      .subscribe(result => {
        if (!result) return;

        if (result.isSuccess) {
          this.notificationsController.success(
            this.t(
              'tlc.positions.position_details_dialog.tabs.employees.assign_members_dialog.notification_success',
            ),
          );
          return;
        }

        this.notificationsController.error(this.resolveFailureMessage(result.action.payload));
      });
  }

  public unassignMember(member: TellentPositionMember): void {
    this.positionsDialogService
      .openUnassignMemberDialog(this.positionData()!, member)
      .pipe(take(1))
      .subscribe();
  }

  public viewMemberProfile(memberGuid: string): void {
    this.redirectService.openEmployeeProfile(memberGuid);
  }

  private confirmAssignment(membersGuids: string[]) {
    if (this.hasPositionsHistoryFlag()) {
      return this.confirmPositionChange(membersGuids);
    }

    return this.confirmReassignment(membersGuids);
  }

  private confirmReassignment(membersGuids: string[]) {
    const conflicts = this.positionsConflictsService.findMembersAssignedElsewhere(
      membersGuids,
      this.positionData()!.guid,
    );

    if (!conflicts.length) {
      return this.doAssignMembers(membersGuids);
    }

    return this.positionsDialogService
      .openReassignMemberWarningDialog({
        targetPositionName: this.positionData()!.name,
        members: conflicts,
      })
      .pipe(
        take(1),
        switchMap(warningResult => {
          if (!warningResult?.confirmed) return EMPTY;
          return this.doAssignMembers(membersGuids);
        }),
      );
  }

  private confirmPositionChange(membersGuids: string[]) {
    return this.positionsDialogService
      .openConfirmPositionChangeDialog({
        targetPositionName: this.positionData()!.name,
        members: this.buildPositionChangeMembers(membersGuids),
      })
      .pipe(
        take(1),
        switchMap(result => {
          if (!result?.confirmed) return EMPTY;
          if (!result.addToHistory) {
            return this.positionsDataFacade.correctMembersPosition(
              this.positionData()!.guid,
              membersGuids,
            );
          }
          return this.doAssignMembers(membersGuids, { startDate: result.effectiveDate });
        }),
      );
  }

  private buildPositionChangeMembers(membersGuids: string[]): ConfirmPositionChangeMember[] {
    const conflicts = this.positionsConflictsService.findMembersAssignedElsewhere(
      membersGuids,
      this.positionData()!.guid,
    );
    const currentPositionByGuid = new Map(
      conflicts.map(conflict => [conflict.member.guid, conflict.currentPosition]),
    );

    return membersGuids.map(guid => {
      const member = this.membersDataFacade.teamMemberById(guid)();
      return {
        guid,
        fullName: member ? resolveMemberFullName(member) : '',
        avatarThumbUrl: member?.avatarThumbUrl ?? null,
        currentPosition: currentPositionByGuid.get(guid) ?? null,
      };
    });
  }

  private doAssignMembers(membersGuids: string[], options?: AssignMembersOptions) {
    return this.positionsDataFacade.assignMembers(this.positionData()!.guid, membersGuids, options);
  }

  // A rejected reassign answers with `{ errors: [{ memberGuid, reason }] }`, where `reason` is
  // already translated and a request-level rule repeats it for every member. One toast carries one
  // message: the reason when the whole failure has a single one, else the generic message — naming
  // one member's reason out of several would imply it is the only problem.
  // Assign failures reach this too, and `assign` does not answer in that shape, so they always
  // resolve to the generic message.
  private resolveFailureMessage(payload: unknown): string {
    return (
      resolveAssignmentFailureReason(payload) ??
      this.t('tlc.positions.confirm_position_change_dialog.notification_error')
    );
  }
}
